import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { useUserProfile } from '@/context/UserProfileContext';
import type { ProfileSummary } from '@/types/profile';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonRouterLink, IonToolbar } from '@ionic/react';
import { getCountryData, type TCountryCode } from 'countries-list';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import AvatarUpload from './AvatarUpload';
import FriendshipButton from './FriendshipButton';
import GolfIcon from './GolfIcon';
import ProfileMessageButton from './ProfileMessageButton';
import { ProfileNavButtons } from './ProfileNavButtons';
import SectionTitle from './SectionTitle';
import ShareDialog from './ShareDialog';
import { SocialLinks } from './SocialLinks';
import { ProfileStats } from './ProfileStats';
import { Button } from './ui/button';
import { GolfAvatar } from './ui/GolfAvatar';
import UserBag from './UserBag';
import { UserProfileMedia } from './UserProfileMedia';
import { UserAchievementsPreview } from './achievements/UserAchievementsPreview';
import { arrowBackOutline } from 'ionicons/icons';
const NeoMap = React.lazy(() => import('./NeoMap'));
type ProfilePageProps = {
  edit?: boolean;
  data: ProfileSummary;
  profileDetailed?: ProfileSummary;
  postsLoading?: boolean;
  loadMorePosts?: () => void;
  hasMorePosts?: boolean;
};

export default function UserProfile({ edit, data, profileDetailed, postsLoading = false, loadMorePosts, hasMorePosts = false }: ProfilePageProps) {
  document.title = data.profile.username;
  const { profile: currentUserProfile } = useUserProfile();
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const profileHeaderRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);

  // Set up intersection observer for sticky header with animation
  useEffect(() => {
    if (!nameRef.current || !headerRef.current) return;

    // Set a negative top margin to trigger header only after scrolling past the name
    const rootMargin = '-20px 0px 0px 0px';

    // Debounce timer and state tracking
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let lastKnownState = isHeaderSticky;
    let consecutiveCount = 0;

    const updateStickyHeader = (isSticky: boolean) => {
      // If state is already what we want, do nothing
      if ((isHeaderSticky && isSticky) || (!isHeaderSticky && !isSticky)) {
        return;
      }

      // Check if this is a rapid toggle (potential flicker)
      if (isSticky !== lastKnownState) {
        consecutiveCount = 0; // Reset counter on state change
      } else {
        consecutiveCount++;
      }

      lastKnownState = isSticky;

      // Clear any existing timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Debounce the state change to prevent flickering
      // Use a shorter delay if we've seen multiple consecutive same states
      const delay = consecutiveCount > 2 ? 50 : 150;

      debounceTimer = setTimeout(() => {
        // Only update if we're still in the same desired state
        if (lastKnownState === isSticky) {
          // Start animation
          setIsAnimating(true);

          // Update state which will change the layout
          setIsHeaderSticky(isSticky);

          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            // Allow animation to complete
            setTimeout(() => {
              setIsAnimating(false);
            }, 300); // Match duration in CSS
          });
        }
      }, delay);
    };

    // Create the observer with a threshold array for smoother transitions
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          // When the name is not visible (scrolled past it), show the header
          if (!entry.isIntersecting) {
            updateStickyHeader(true); // Show sticky header
          } else {
            updateStickyHeader(false); // Hide sticky header when name is visible
          }
        });
      },
      { threshold: [0, 0.5], rootMargin }
    );

    observer.observe(nameRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isHeaderSticky]);

  // Check if user is authenticated
  useEffect(() => {
    // If currentUserProfile exists, the user is authenticated
    setIsSignedIn(!!currentUserProfile);
  }, [currentUserProfile]);

  // NeoMap is already lazy loaded at the top level
  const { t } = useTranslation();
  const [userId, setUserId] = useState<string | null>(null);
  const { profile } = data;
  const { membershipCourse } = data;
  const { posts } = data;

  useEffect(() => {
    const getCurrentUser = async () => {
      const result = await FirebaseAuthentication.getCurrentUser();
      setUserId(result.user?.uid || null);
    };
    getCurrentUser();
  }, []);

  return (
    <>
      <IonHeader className="ion-no-border">
        {/* Sticky header that appears when scrolling past the name */}
        <div
          ref={headerRef}
          className={`fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-black/90 backdrop-blur-md md:hidden transition-transform duration-200 pt-safe ease-out ${isHeaderSticky ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[-300%]'}`}
        >
          <div className="flex items-center px-4 relative">
            {/* Back button - always visible */}
            <IonButtons slot="start" className="absolute left-2 z-10">
              <IonBackButton icon={arrowBackOutline} color="" text="" className='text-foreground' defaultHref="/dashboard" />
            </IonButtons>

            {/* Avatar with transition class */}
            <div
              className={`relative overflow-hidden rounded-full transition-all duration-300 ease-out ${isAnimating ? 'animate-avatar' : ''} w-8 h-8 ml-8`}
            >
              <img
                src={profile.imageUrl}
                alt={profile.username}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `/assets/images/user.webp`;
                }}
              />
            </div>

            {/* Username with transition class */}
            <div
              className={`ml-3 font-medium transition-all duration-300 ease-out ${isHeaderSticky ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
            >
              {`${profile?.firstName} ${profile.lastName}`}
            </div>

            {/* Right side buttons */}
            <div className="ml-auto">
              {isSignedIn && profileDetailed && (
                <IonButton fill="clear" routerLink='/menu'>
                  <GolfIcon name="settings" className='size-6 text-foreground' />
                </IonButton>
              )}
            </div>
          </div>
        </div>
      </IonHeader>
      <IonContent fullscreen={true}>
        <div className="flex w-full flex-col md:rounded-lg mx-auto max-w-3xl">
          <div ref={profileHeaderRef} id="profile-header" className="relative md:to-background bg-gradient-to-t to-primary/20 dark:to-foreground/20 from-transparent pt-safe md:mt-0">
            {/* Origin Toolbar */}
            <IonToolbar color='transparent' className='px-3 md:hidden'>
              <IonButtons slot="start">
                <IonBackButton icon={arrowBackOutline} text="" className='text-black bg-white/60 backdrop-blur-sm rounded-full size-10 pl-1 text-xs' defaultHref="/dashboard" />
              </IonButtons>
              <IonButtons slot="end">
                {isSignedIn && profileDetailed && (
                  <IonButton fill="clear" routerLink='/menu'>
                    <GolfIcon name="settings" className='size-6 text-foreground' />
                  </IonButton>
                )}
              </IonButtons>
            </IonToolbar>

            {/* Avatar and Profile Info - Instagram-like layout */}
            <div className="px-4 py-6 flex flex-col">
              {/* Top row: Avatar and basic info */}
              <div className="flex flex-row items-start gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {(edit || !userId || userId !== profile.userId) ? (
                    <GolfAvatar
                      canOpen={!edit}
                      size={80}
                      imageUrl={profile.imageUrl!}
                      name={profile.firstName || 'UK'}
                      className="border-2 border-card md:size-[128px]"
                    />
                  ) : (
                    <AvatarUpload
                      initialImage={profile.imageUrl!}
                      className="size-20 border-2 border-card md:size-[128px]"
                    />
                  )}
                </div>

                {/* Name, verification and country */}
                <div className="flex-1">
                  <div className="flex items-center">
                    <h1 ref={nameRef} className="text-xl font-semibold md:text-3xl">
                      {`${profile?.firstName} ${profile.lastName}`}
                    </h1>
                    {profile?.verified && <img src="/assets/images/icons/verified.svg" className='size-5 ml-1 rounded-full md:size-8' />}
                  </div>

                  {profile.countryCode && (
                    <div className="flex items-center text-sm text-muted-foreground gap-1 mt-1 md:text-lg md:gap-2">
                      <img
                        src={`/assets/images/flags/${profile.countryCode}.svg`}
                        alt={profile.countryCode}
                        className="size-4 border border-white object-cover rounded-full md:size-5"
                      />
                      <span>
                        {getCountryData(profile.countryCode as TCountryCode).name}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Description - below avatar and info */}
              <div className="mt-4">
                <p className="text-muted-foreground text-sm md:text-base">
                  {profile?.description}
                  {membershipCourse?.name && <span className="block mt-1">{membershipCourse.name}</span>}
                </p>
              </div>

              <ProfileStats
                followersCount={profile.followersCount}
                followingCount={profile.followingCount}
                totalTrips={profileDetailed?.totalGames}
                handicap={profile.handicap}
              />

              {/* Action buttons */}
              <div className="flex flex-row items-center mt-4 gap-2">
                {userId === profile.userId ? (
                  <>
                    <IonButton fill='outline' routerLink='/settings/edit-profile' type="button" className='text-muted-foreground flex-1 md:flex-initial active:bg-none' >
                      {t('edit_profile')}
                    </IonButton>
                    <ShareDialog username={profile.username} />
                  </>
                ) : (
                  <>
                    {userId && (
                      <div className="flex gap-2">
                        <FriendshipButton userId={profile.userId} />
                        <ProfileMessageButton userId={profile.userId} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <SocialLinks profile={profile} />
          </div>
          {/* Tabs Section */}
          <Tabs defaultValue="infos" className="w-full">
            <TabsList
              color="primary"
              className={`grid w-full grid-cols-2 sticky md:static z-40 bg-white/90 dark:bg-black/90 backdrop-blur-md border-y border-t-0 transition-all duration-200 ${isHeaderSticky ? 'top-safe-offset-4' : 'top-0'}`}
            >
              <TabsTrigger value="infos" className="flex items-center gap-2">
                <span className="md:inline">{t('profile')}</span>
              </TabsTrigger>
              <TabsTrigger value="medias">
                <span className="md:inline">{t('medias')}</span>
              </TabsTrigger>
            </TabsList>

            {/* Infos Tab */}
            <TabsContent value="infos">
              {/* Strava-like navigation buttons for mobile */}

              <SectionTitle className="m-4 mb-6">{t('played_courses')}</SectionTitle>
              <Suspense fallback={<div className="h-[300px] w-full animate-pulse bg-neutral-200 dark:bg-none"></div>}>
                <NeoMap visitedCountries={profileDetailed?.stats.countriesVisited} />
              </Suspense>

              {/* Show UserBag on desktop, but not on mobile since we have the dedicated button */}
              {/* <UserBag userId={profile.userId} limit={4} showEditButton={profile.id === currentUserProfile?.id} /> */}

              <div className='bg-secondary dark:bg-background h-2'></div>
              <ProfileNavButtons userId={profile.userId} />
              <div className='bg-secondary dark:bg-background h-2'></div>
              <UserAchievementsPreview />
              {/* <div className='bg-secondary dark:bg-primary h-2'></div> */}
              {/* <RecentScorecards /> */}
            </TabsContent>

            {/* Medias Tab */}
            <TabsContent value="medias">
              <div className="text-center text-xl text-muted-foreground">
                {posts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-4 md:hidden">
                    <div className="flex h-full gap-2 flex-col items-center justify-center">
                      <img src="/assets/images/NoImages.svg" alt="No messages" />
                      <p className="text-lg font-medium text-foreground">{t('no_images_yet')}</p>
                    </div>
                  </div>
                ) : (
                  <UserProfileMedia posts={posts} loading={postsLoading} hasMore={hasMorePosts} onLoadMore={loadMorePosts} />
                )}
              </div>
            </TabsContent>
          </Tabs>
          {edit || userId
            ? (<div></div>)
            : (
              <div className="m-auto p-8 align-middle">
                <IonRouterLink href="/sign-up">
                  <Button type="button" className="relative inline-flex justify-center whitespace-nowrap rounded-full bg-gradient-to-r from-slate-800 to-slate-700 px-3.5 py-2.5 text-sm font-medium text-slate-200 shadow before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,theme(colors.white/.5)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-[position:200%_0,0_0] before:bg-no-repeat before:[transition:background-position_0s_ease] hover:before:bg-[position:-100%_0,0_0] focus:outline-none focus:ring focus:ring-slate-500/50 focus-visible:outline-none focus-visible:ring focus-visible:ring-slate-500/50 dark:from-slate-200 dark:to-slate-100 dark:text-slate-800 dark:before:bg-[linear-gradient(45deg,transparent_25%,theme(colors.white)_50%,transparent_75%,transparent_100%)] dark:hover:bg-slate-100">
                    {t('create_your_profile')}
                  </Button>
                </IonRouterLink>
              </div>
            )}
        </div>
      </IonContent >
    </>
  );
}